import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { Formik, Form, FieldArray } from 'formik';
import { Grid, Header, Button, Icon, Message, Segment, Divider, Container } from 'semantic-ui-react';
import { pathDefinitionValidationSchema, debugPathDefinitionValidationSchema } from '../../validation/pathRegistrySchemas';
import {
  FormikInput,
  FormikTextArea,
  FormikSelect,
  FormikField,
  FormikCheckbox
} from '../../pages/subPages/voiceActor/FormComponents';
import {
  fetchPathDefinition,
  createPathDefinition,
  updatePathDefinition,
  clearError,
  clearSuccess
} from '../../slices/pathRegistrySlice';

import { AuthStdFooter } from "../../utils/auth-std-footer";
/**
 * PathDefinitionForm component for creating and editing path definitions
 * @returns {JSX.Element} PathDefinitionForm component
 */
const PathDefinitionForm = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { name } = useParams();
  const isEditing = !!name;

  // Get path definition data from Redux
  const { currentPathDefinition, loading, error, success } = useSelector(
    (state) => state.user.pathRegistry
  );

  // Local state for form management
  const [formLoading, setFormLoading] = useState(false);
  const [localError, setLocalError] = useState(null);
  const [localSuccess, setLocalSuccess] = useState(null);

  // Fetch path definition data if editing
  useEffect(() => {
    if (isEditing && name) {
      dispatch(fetchPathDefinition(name));
    }
  }, [dispatch, isEditing, name]);

  // Update local state when Redux state changes
  useEffect(() => {
    if (error) {
      setLocalError(error);
      setTimeout(() => {
        dispatch(clearError());
        setLocalError(null);
      }, 5000);
    }

    if (success) {
      setLocalSuccess(success);
      setTimeout(() => {
        dispatch(clearSuccess());
        setLocalSuccess(null);
      }, 5000);
    }
  }, [dispatch, error, success]);

  // Prepare initial form values
  const getInitialValues = () => {
    if (isEditing && currentPathDefinition) {
      // Convert requiredSegments from array to comma-separated string for the form
      const requiredSegmentsString = Array.isArray(currentPathDefinition.pattern.requiredSegments)
        ? currentPathDefinition.pattern.requiredSegments.join(', ')
        : '';

      return {
        ...currentPathDefinition,
        pattern: {
          ...currentPathDefinition.pattern,
          requiredSegmentsString
        }
      };
    }

    // Default values for new path definition
    return {
      name: '',
      description: '',
      active: true,
      pattern: {
        requiredSegmentsString: '',
        folderTemplate: '',
        requestType: 'FOLDER'
      },
      params: []
    };
  };

  // Handle form submission
  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    setFormLoading(true);
    setLocalError(null);
    setLocalSuccess(null);

    try {
      // Convert requiredSegmentsString back to array
      const requiredSegments = values.pattern.requiredSegmentsString
        .split(',')
        .map(segment => segment.trim())
        .filter(Boolean);

      // Coerce param types the form leaves loose so UI-created defs match the
      // .json-seeded convention (the server stores these verbatim):
      //  - `position` comes from a text input as a STRING → force to a Number.
      //  - an "empty" `defaultValue` is sometimes typed as the literal `""`
      //    (two quote chars) → normalize that (and whitespace-only) to ''.
      const params = (values.params || []).map((p) => ({
        ...p,
        position: Number(p.position),
        defaultValue:
          p.defaultValue == null || String(p.defaultValue).trim() === '""'
            ? ''
            : p.defaultValue,
      }));

      // Prepare data for submission
      const pathDefData = {
        ...values,
        params,
        pattern: {
          ...values.pattern,
          requiredSegments,
          // Remove the string version that was only used in the form
          requiredSegmentsString: undefined
        }
      };

      if (isEditing) {
        // Update existing path definition
        await dispatch(updatePathDefinition({
          name,
          updates: pathDefData
        })).unwrap();
        setLocalSuccess('Path definition updated successfully');
      } else {
        // Create new path definition
        await dispatch(createPathDefinition(pathDefData)).unwrap();
        setLocalSuccess('Path definition created successfully');
        resetForm();
      }
    } catch (err) {
      setLocalError(err.message || 'An error occurred');
    } finally {
      setFormLoading(false);
      setSubmitting(false);
    }
  };

  // Handle cancel button click
  const handleCancel = () => {
    navigate('/admin/path-registry');
  };

  return (
    <div className="page-container" style={{ marginBottom: "70px" }}>
      <Container>
        <Segment>
          <Header as="h2">
            <Icon name="sitemap" />
            <Header.Content>
              {isEditing ? 'Edit Path Definition' : 'Create New Path Definition'}
              <Header.Subheader>
                {isEditing
                  ? `Editing path definition: ${name}`
                  : 'Create a new path definition for S3 path resolution'}
              </Header.Subheader>
            </Header.Content>
          </Header>
          <Divider />

          {/* Navigation buttons */}
          <div style={{ marginBottom: '1em', display: 'flex', justifyContent: 'space-between' }}>
            <Button
              icon
              labelPosition="left"
              onClick={handleCancel}
            >
              <Icon name="arrow left" />
              Back to Path Registry
            </Button>
            {isEditing && (
              <Button
                as={Link}
                to={`/admin/path-registry/${name}/view`}
                color="blue"
                icon
                labelPosition="left"
              >
                <Icon name="eye" />
                View Details
              </Button>
            )}
          </div>

          {loading && !currentPathDefinition && isEditing ? (
            <Message info>
              <Message.Header>Loading...</Message.Header>
              <p>Fetching path definition data</p>
            </Message>
          ) : (
            <Formik
              initialValues={getInitialValues()}
              validationSchema={debugPathDefinitionValidationSchema} // Using debug version for troubleshooting
              onSubmit={handleSubmit}
              enableReinitialize
            >
              {({ values, isSubmitting, isValid, dirty, setFieldValue }) => (
                <Form>
                  <Grid>
                    {/* Basic Information */}
                    <Grid.Row>
                      <Grid.Column width={16}>
                        <Header as="h3" dividing>Basic Information</Header>
                      </Grid.Column>
                    </Grid.Row>

                    <Grid.Row>
                      <Grid.Column width={8}>
                        <FormikField
                          name="name"
                          component={FormikInput}
                          label="Path Definition Name"
                          fluid
                          placeholder="e.g., adminProfileImage"
                          disabled={isEditing} // Name cannot be changed when editing
                        />
                      </Grid.Column>
                      <Grid.Column width={8}>
                        <FormikField
                          name="active"
                          component={FormikCheckbox}
                          label="Active"
                        />
                      </Grid.Column>
                    </Grid.Row>

                    <Grid.Row>
                      <Grid.Column width={16}>
                        <FormikField
                          name="description"
                          component={FormikTextArea}
                          label="Description"
                          placeholder="Describe the purpose of this path definition"
                          rows={3}
                        />
                      </Grid.Column>
                    </Grid.Row>

                    {/* Pattern Information */}
                    <Grid.Row>
                      <Grid.Column width={16}>
                        <Header as="h3" dividing>Pattern Information</Header>
                      </Grid.Column>
                    </Grid.Row>

                    <Grid.Row>
                      <Grid.Column width={16}>
                        <FormikField
                          name="pattern.requiredSegmentsString"
                          component={FormikInput}
                          label="Required Segments (comma-separated)"
                          fluid
                          placeholder="e.g., aai-users, administrators, profile-img"
                        />
                      </Grid.Column>
                    </Grid.Row>

                    <Grid.Row>
                      <Grid.Column width={12}>
                        <FormikField
                          name="pattern.folderTemplate"
                          component={FormikInput}
                          label="Folder Template"
                          fluid
                          placeholder="e.g., administrators/{adminId}/{folderName}"
                        />
                      </Grid.Column>
                      <Grid.Column width={4}>
                        <FormikField
                          name="pattern.requestType"
                          component={FormikSelect}
                          label="Request Type"
                          options={[
                            { key: 'folder', text: 'FOLDER', value: 'FOLDER' },
                            { key: 'file', text: 'FILE', value: 'FILE' }
                          ]}
                          fluid
                        />
                      </Grid.Column>
                    </Grid.Row>

                    {/* Parameters */}
                    <Grid.Row>
                      <Grid.Column width={16}>
                        <Header as="h3" dividing>Parameters</Header>
                        <FieldArray name="params">
                          {({ push, remove }) => (
                            <>
                              {values.params && values.params.length > 0 ? (
                                values.params.map((param, index) => (
                                  <Segment key={index}>
                                    <Grid>
                                      <Grid.Row>
                                        <Grid.Column width={3}>
                                          <FormikField
                                            name={`params.${index}.name`}
                                            component={FormikInput}
                                            label="Name"
                                            fluid
                                            placeholder="e.g., fileName"
                                          />
                                        </Grid.Column>
                                        <Grid.Column width={2}>
                                          <FormikField
                                            name={`params.${index}.position`}
                                            component={FormikInput}
                                            label="Position"
                                            type="number"
                                            fluid
                                            placeholder="-1"
                                          />
                                        </Grid.Column>
                                        <Grid.Column width={2}>
                                          <FormikField
                                            name={`params.${index}.required`}
                                            component={FormikCheckbox}
                                            label="Required"
                                          />
                                        </Grid.Column>
                                        <Grid.Column width={2}>
                                          <FormikField
                                            name={`params.${index}.isCritical`}
                                            component={FormikCheckbox}
                                            label="Critical"
                                          />
                                        </Grid.Column>
                                        <Grid.Column width={3}>
                                          <FormikField
                                            name={`params.${index}.defaultValue`}
                                            component={FormikInput}
                                            label="Default Value"
                                            fluid
                                            placeholder="Default value"
                                          />
                                        </Grid.Column>
                                        <Grid.Column width={3}>
                                          <FormikField
                                            name={`params.${index}.description`}
                                            component={FormikInput}
                                            label="Description"
                                            fluid
                                            placeholder="Parameter description"
                                          />
                                        </Grid.Column>
                                        <Grid.Column width={1} verticalAlign="bottom">
                                          <Button
                                            icon
                                            negative
                                            type="button"
                                            onClick={() => remove(index)}
                                            style={{ marginBottom: '1em' }}
                                          >
                                            <Icon name="trash" />
                                          </Button>
                                        </Grid.Column>
                                      </Grid.Row>
                                    </Grid>
                                  </Segment>
                                ))
                              ) : (
                                <Message info>
                                  <p>No parameters defined. Click the button below to add a parameter.</p>
                                </Message>
                              )}
                              <Button
                                type="button"
                                onClick={() => push({
                                  name: '',
                                  position: -1,
                                  required: true,
                                  isCritical: false,
                                  defaultValue: '',
                                  description: ''
                                })}
                                icon
                                labelPosition="left"
                                primary
                                style={{ marginTop: '1em' }}
                              >
                                <Icon name="plus" />
                                Add Parameter
                              </Button>
                            </>
                          )}
                        </FieldArray>
                      </Grid.Column>
                    </Grid.Row>

                    {/* Messages */}
                    {localSuccess && (
                      <Grid.Row>
                        <Grid.Column width={16}>
                          <Message positive>
                            <Message.Header>Success</Message.Header>
                            <p>{localSuccess}</p>
                          </Message>
                        </Grid.Column>
                      </Grid.Row>
                    )}

                    {localError && (
                      <Grid.Row>
                        <Grid.Column width={16}>
                          <Message negative>
                            <Message.Header>Error</Message.Header>
                            <p>{localError}</p>
                          </Message>
                        </Grid.Column>
                      </Grid.Row>
                    )}

                    {/* Debug Information */}
                    <Grid.Row>
                      <Grid.Column width={16}>
                        <Message info>
                          <Message.Header>Debug Information</Message.Header>
                          <p>
                            <strong>Form State:</strong>
                            isSubmitting: {isSubmitting.toString()},
                            isValid: {isValid.toString()},
                            dirty: {dirty.toString()}
                          </p>
                          <p>
                            <strong>Button Disabled Condition:</strong> {(isSubmitting || !isValid || !dirty).toString()}
                          </p>
                          <p>
                            <strong>Form Values:</strong>
                          </p>
                          <pre style={{ maxHeight: '200px', overflow: 'auto' }}>
                            {JSON.stringify(values, null, 2)}
                          </pre>
                          <p>
                            <strong>Check the browser console for validation logs</strong>
                          </p>
                          <div>
                            <Button
                              type="button"
                              onClick={() => {
                                // Force the form to be dirty
                                setFieldValue('description', values.description + ' ');
                                setTimeout(() => {
                                  setFieldValue('description', values.description.trim());
                                }, 100);

                                // Log form values for debugging
                                console.log('Current form values:', values);

                                // Check if all required fields are filled
                                const requiredFields = [
                                  'name',
                                  'description',
                                  'pattern.requiredSegmentsString',
                                  'pattern.folderTemplate',
                                  'pattern.requestType'
                                ];

                                let missingFields = [];
                                requiredFields.forEach(field => {
                                  const fieldParts = field.split('.');
                                  let value = values;
                                  for (const part of fieldParts) {
                                    value = value[part];
                                  }
                                  if (!value) {
                                    missingFields.push(field);
                                  }
                                });

                                if (missingFields.length > 0) {
                                  console.log('Missing required fields:', missingFields);
                                } else {
                                  console.log('All required fields are filled');
                                }

                                // Check parameters
                                if (!values.params || values.params.length === 0) {
                                  console.log('No parameters defined');
                                } else {
                                  console.log('Parameters:', values.params);
                                  values.params.forEach((param, index) => {
                                    const paramMissingFields = [];
                                    if (!param.name) paramMissingFields.push('name');
                                    if (param.position === undefined) paramMissingFields.push('position');
                                    if (param.required === undefined) paramMissingFields.push('required');
                                    if (!param.description) paramMissingFields.push('description');

                                    if (paramMissingFields.length > 0) {
                                      console.log(`Parameter ${index} missing fields:`, paramMissingFields);
                                    }
                                  });
                                }
                              }}
                              style={{ marginRight: '1em' }}
                            >
                              Force Validation Check
                            </Button>

                            <Button
                              type="button"
                              color="green"
                              onClick={() => {
                                // This is a workaround to force the form to be valid and dirty
                                // It will bypass the validation temporarily

                                // First, make sure all required fields have values
                                if (!values.name) setFieldValue('name', 'adminProfileImage');
                                if (!values.description) setFieldValue('description', 'Path definition for admin profile images');
                                if (!values.pattern.requiredSegmentsString) setFieldValue('pattern.requiredSegmentsString', 'aai-users,administrators,profile-img');
                                if (!values.pattern.folderTemplate) setFieldValue('pattern.folderTemplate', 'administrators/{adminId}/{folderName}');
                                if (!values.pattern.requestType) setFieldValue('pattern.requestType', 'FILE');

                                // Make sure we have at least one parameter
                                if (!values.params || values.params.length === 0) {
                                  setFieldValue('params', [
                                    {
                                      name: 'adminId',
                                      position: -1,
                                      required: true,
                                      isCritical: true,
                                      defaultValue: '',
                                      description: 'UUID of admin user'
                                    },
                                    {
                                      name: 'folderName',
                                      position: -2,
                                      required: true,
                                      isCritical: false,
                                      defaultValue: 'profile-img',
                                      description: 'Folder name where images are kept'
                                    },
                                    {
                                      name: 'fileName',
                                      position: -3,
                                      required: true,
                                      isCritical: false,
                                      defaultValue: '',
                                      description: 'File name to be fetched'
                                    }
                                  ]);
                                } else {
                                  // Make sure all parameters have required fields
                                  values.params.forEach((param, index) => {
                                    if (!param.name) setFieldValue(`params.${index}.name`, `param${index}`);
                                    if (param.position === undefined) setFieldValue(`params.${index}.position`, -1 - index);
                                    if (param.required === undefined) setFieldValue(`params.${index}.required`, true);
                                    if (!param.description) setFieldValue(`params.${index}.description`, `Parameter ${index} description`);
                                  });
                                }

                                // Force the form to be dirty
                                setTimeout(() => {
                                  setFieldValue('description', values.description + ' ');
                                  setTimeout(() => {
                                    setFieldValue('description', values.description.trim());
                                  }, 100);
                                }, 100);

                                console.log('Form values have been set to valid defaults');
                              }}
                            >
                              Force Enable Create Button
                            </Button>
                          </div>
                        </Message>
                      </Grid.Column>
                    </Grid.Row>

                    {/* Submit Button */}
                    <Grid.Row>
                      <Grid.Column width={16}>
                        <Button.Group>
                          <Button
                            primary
                            type="submit"
                            loading={formLoading}
                            disabled={isSubmitting || !isValid || !dirty}
                          >
                            {isEditing ? 'Update' : 'Create'} Path Definition
                          </Button>
                          <Button.Or />
                          <Button type="button" onClick={handleCancel}>
                            Cancel
                          </Button>
                        </Button.Group>
                      </Grid.Column>
                    </Grid.Row>
                  </Grid>
                </Form>
              )}
            </Formik>
          )}
        </Segment>
      </Container>
      {AuthStdFooter()}
    </div>
  );
};

export default PathDefinitionForm;
